<script lang="ts" generics="T">
  import type { Snippet } from "svelte";

  interface Props {
    items: T[];
    item: Snippet<[T]>;
    getLabel: (item: T) => string;
    getDescription?: (item: T) => string | undefined;
    matchFn?: (item: T, query: string) => boolean;
    searchPlaceholder?: string;
    searchable?: boolean;
    threshold?: number;
    maxHeight?: string;
    autoFocus?: boolean;
    emptyText?: string;
    class?: string;
  }

  const {
    items,
    item,
    getLabel,
    getDescription,
    matchFn,
    searchPlaceholder = "Type to search...",
    searchable = true,
    threshold = 0,
    maxHeight = "240px",
    autoFocus = false,
    emptyText,
    class: className,
  }: Props = $props();

  let searchQuery = $state("");
  let searchInput = $state<HTMLInputElement>();
  let listContainer = $state<HTMLDivElement>();

  const defaultMatch = (entry: T, q: string) => {
    const haystack = `${getLabel(entry)} ${getDescription?.(entry) ?? ""}`.toLowerCase();
    return haystack.includes(q);
  };

  const filtered = $derived.by(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return items;
    const match = matchFn ?? defaultMatch;
    return items.filter((entry) => match(entry, q));
  });

  const showSearch = $derived(searchable && items.length > threshold);

  $effect(() => {
    if (autoFocus && searchInput) {
      requestAnimationFrame(() => searchInput?.focus());
    }
  });

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      const items = listContainer?.querySelectorAll<HTMLElement>('[role="menuitem"]');
      if (items?.length === 1) {
        items[0].click();
      } else if (items && items.length > 0) {
        items[0].focus();
      }
      return;
    }
    if (!["Escape", "ArrowDown", "ArrowUp", "Tab"].includes(e.key)) {
      e.stopPropagation();
    }
  }
</script>

<div class={className}>
  {#if showSearch}
    <div class="mb-1 pb-1">
      <input
        bind:this={searchInput}
        type="text"
        bind:value={searchQuery}
        onkeydown={handleKeydown}
        placeholder={searchPlaceholder}
        class="w-full rounded-xl border-0 bg-mono-000/10 px-2 py-1.5 text-sm text-mono-000 outline-none placeholder:text-mono-000/50 focus:ring-1 focus:ring-mono-000/50 dark:bg-mono-900/10 dark:text-mono-900 dark:placeholder:text-mono-900/50"
      />
    </div>
  {/if}
  <div
    bind:this={listContainer}
    style:max-height={maxHeight}
    class="overflow-y-auto bg-transparent"
  >
    {#each filtered as entry}
      {@render item(entry)}
    {:else}
      {#if emptyText}
        <div class="px-2 py-3 text-center text-sm text-mono-000 dark:text-mono-900/50">
          {emptyText}
        </div>
      {/if}
    {/each}
  </div>
</div>
